import { useState } from 'react';
import { Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import Animated, { SlideInRight } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { cn } from '@/lib/utils';
import type { BaseModalProps } from '@/contexts/ModalContext';
import { Icon } from '~/global/components/icon/Icon';
import { useColor } from '~/global/hooks/useColor';
import { useKeyboard } from '~/global/hooks/useKeyboard';
import type { RightSidebarTab } from '~/layout/contexts/right-sidebar-layout/RightSidebarLayout';
import { RightSidebarDrawerProvider } from '~/layout/components/right-sidebar/RightSidebarDrawerContext';
import { RightSidebarCloseButton } from '~/layout/components/right-sidebar/RightSidebarCloseButton';

export interface RightSidebarDrawerProps extends BaseModalProps {
  /**
   * Rail tabs to host, snapshotted by {@link RightSidebar.Trigger}. Passed in
   * (not read from `useRightSidebar()`) because the drawer renders at the app
   * root via `useModal()` — above `RightSidebarLayoutProvider` in the tree — so
   * the context is not in scope here. Optional only to stay assignable to the
   * `useModal()` component type; the trigger always supplies it.
   */
  tabs?: RightSidebarTab[];
  /** Tab to open on; defaults to the first. */
  initialKey?: string | null;
}

/**
 * Generic right-side drawer that hosts whatever rail the current screen
 * registered — not tied to chat/meal/workout. The mobile counterpart of the
 * desktop {@link RightSidebar.Rail} + panel, opened by {@link RightSidebar.Trigger}.
 *
 * Presented through {@link useModal}, so it renders as a plain overlay at the app
 * root — in the main window (not a React Native `Modal`) and outside the root
 * `SafeAreaView`. That matters because the chat rows use a native SwiftUI context
 * menu (`@expo/ui`) which mis-measures inside an RN `Modal`'s separate window,
 * and the root `SafeAreaView edges={['top']}` consumes the top inset — so insets
 * are read here via `useSafeAreaInsets()` and applied as panel padding.
 *
 * Layout follows the requirement:
 *  - 2+ rail tabs → an icons-only rail strip on top (active highlighted) with the
 *    close button at its right end; the active panel renders below.
 *  - ≤1 tab → no strip; the single panel renders and the close button is injected
 *    into its {@link RightSidebar.PanelHeader} via the drawer scope.
 */
export const RightSidebarDrawer = ({
  tabs = [],
  initialKey,
  onClose,
}: RightSidebarDrawerProps) => {
  const { width } = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();
  const keyboard = useKeyboard();
  const iconColor = useColor('text-primary');

  const hasStrip = tabs.length > 1;

  // Local — the drawer is a transient mobile surface and lives outside the
  // right-sidebar provider, so it doesn't touch the globally-persisted tool.
  const [activeKey, setActiveKey] = useState<string | null>(
    (initialKey && tabs.some((tab) => tab.key === initialKey)
      ? initialKey
      : tabs[0]?.key) ?? null,
  );

  const activePanel =
    tabs.find((tab) => tab.key === activeKey)?.panel ?? tabs[0]?.panel ?? null;

  // With a rail strip the drawer is a full-screen workspace; the single-panel
  // drawer stays a narrower side sheet.
  const panelWidth = hasStrip ? width : Math.min(width * 0.88, 400);

  if (tabs.length === 0) {
    return null;
  }

  return (
    <View style={StyleSheet.absoluteFill}>
      {/* Backdrop */}
      <Pressable
        style={[
          StyleSheet.absoluteFill,
          { backgroundColor: 'rgba(0,0,0,0.5)' },
        ]}
        onPress={onClose}
      />

      {/* Full-height side panel anchored to the right edge. No border radius when
          it opens from the side. The surface lives on the inner View as a
          `bg-surface` class (same token the desktop panel column uses, so it
          tracks dark mode) — the reanimated `Animated.View` only animates. */}
      <Animated.View
        entering={SlideInRight.duration(300)}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: panelWidth,
        }}
        className="shadow-lg shadow-black/5"
      >
        <View
          className={cn(
            'flex-1 overflow-hidden bg-surface',
            // Tabbed (full-width) drawer gets a touch more side padding.
            hasStrip && 'px-2',
          )}
          // Lift the content (incl. the coach chat's pinned input / search
          // fields) above the keyboard; otherwise pad to the home-indicator inset.
          style={{
            paddingTop: top,
            paddingBottom: keyboard.visible ? keyboard.height : bottom,
          }}
        >
          {hasStrip && (
            <View className="flex-row items-center justify-between gap-2 px-3 py-3">
              <View className="flex-row items-center gap-2">
                {tabs.map((tab) => {
                  const isActive = tab.key === activeKey;
                  return (
                    <Pressable
                      key={tab.key}
                      accessibilityRole="button"
                      accessibilityLabel={tab.label}
                      accessibilityState={{ selected: isActive }}
                      onPress={() => setActiveKey(tab.key)}
                      className={cn(
                        'h-9 w-9 items-center justify-center rounded-lg',
                        isActive
                          ? 'bg-surface-elevated'
                          : 'opacity-60 web:hover:opacity-100',
                      )}
                    >
                      <Icon name={tab.icon} size={20} color={iconColor} />
                    </Pressable>
                  );
                })}
              </View>

              <RightSidebarCloseButton onPress={onClose} />
            </View>
          )}

          <RightSidebarDrawerProvider
            value={{ close: onClose, headerShowsClose: !hasStrip }}
          >
            <View className="flex-1">{activePanel}</View>
          </RightSidebarDrawerProvider>
        </View>
      </Animated.View>
    </View>
  );
};
